import { DCA_EXECUTOR_INTERFACE, ERC20_APPROVE_INTERFACE, WRAP_NATIVE_CALLDATA } from './abi'
import { isDcaNativeAddress } from './api'
import { DCA_CHAIN_ID, DCA_WRAPPED_NATIVE } from './env'
import { DcaCancelSummary, DcaConfig, DcaExecutionStep, DcaPrepareSummary } from './types'

/** Thrown when the backend's plan does not match what the user asked for. */
export class DcaPlanRejectedError extends Error {
  constructor(reason: string) {
    super(`DCA plan rejected: ${reason}`)
    this.name = 'DcaPlanRejectedError'
  }
}

/** What the user entered, i.e. what the plan must encode. */
export type DcaExpectedOrder = {
  /** As picked: `0x0…0` when selling the native coin. */
  tokenIn: string
  tokenOut: string
  /** The total the user typed; the plan may use less (rounding), never more. */
  totalAmountRaw: string
  totalExecutions: number
  interval: number
  maxSlippageBps: number
  /** Unix seconds, 0 = start once mined. */
  startTime: number
}

const STEP_ORDER = ['wrap-native', 'approve', 'approve-2', 'create-order']

/**
 * Ceiling on the keeper fee per buy, in basis points of that buy. The API sends
 * `executionFeeRaw` and the order caps it on-chain, so without a ceiling of our
 * own a compromised API could let the keeper take most of every buy.
 */
export const MAX_EXECUTION_FEE_BPS = 500n

/**
 * Ceiling on an order's slippage, whatever `/dca/config` says. The form fills
 * the slippage from the API in auto mode and bounds it by the API's maximum,
 * so without a ceiling of our own a compromised API could have every buy
 * filled far below the market. Above the contract's 5% so the backend can
 * raise its maximum a little without a wallet release.
 */
export const MAX_DCA_SLIPPAGE_BPS = 1000

/** Deadlines are computed by the backend a moment before we check them. */
const DEADLINE_CLOCK_SLACK = 300

const same = (a: string | null | undefined, b: string | null | undefined) =>
  !!a && !!b && a.toLowerCase() === b.toLowerCase()

const reject = (reason: string): never => {
  throw new DcaPlanRejectedError(reason)
}

/**
 * Check every step the backend wants signed against the form and the executor
 * pinned in this build (DCA client doc §4.1). A single mismatch rejects the
 * whole plan: the API is not trusted to choose the spender, the amounts or the
 * order parameters.
 */
export function verifyDcaPlan({
  steps,
  summary,
  config,
  expected,
  pinnedExecutor,
  currentAllowanceRaw,
  nowSeconds = Math.floor(Date.now() / 1000)
}: {
  steps: DcaExecutionStep[]
  summary: DcaPrepareSummary
  config: DcaConfig
  expected: DcaExpectedOrder
  pinnedExecutor: string
  /**
   * The owner's allowance to the executor on the order token, read on-chain
   * just before the check. The new approval may only add this order's budget
   * on top of it: the API is not trusted to size the approval.
   */
  currentAllowanceRaw: bigint
  nowSeconds?: number
}): void {
  const executor = (config.executorAddress || '').toLowerCase()
  if (!executor || executor !== pinnedExecutor.toLowerCase()) reject('unexpected executor')
  // Pinned like the executor: the wrap step sends PLS to this address.
  if (!same(config.wrappedNativeAddress, DCA_WRAPPED_NATIVE)) reject('unexpected wrapped native')
  if (config.chainId !== DCA_CHAIN_ID) reject('unexpected chain')
  if (!config.limits || config.feeBps === null) reject('DCA config is incomplete')

  const sellsNative = isDcaNativeAddress(expected.tokenIn)
  // On-chain the order always sells an ERC-20: WPLS when the user sells PLS.
  const orderTokenIn = sellsNative ? DCA_WRAPPED_NATIVE : expected.tokenIn

  // --- summary: the numbers the user reviews must be the ones the plan uses ---
  const per = BigInt(summary.amountPerExecutionRaw)
  const total = BigInt(summary.totalAmountRaw)
  if (per <= 0n) reject('amount per execution is zero')
  if (summary.totalExecutions !== expected.totalExecutions) reject('execution count differs')
  if (per * BigInt(expected.totalExecutions) !== total) reject('total differs from per × count')
  if (total > BigInt(expected.totalAmountRaw)) reject('plan spends more than the user entered')
  if (summary.interval !== expected.interval) reject('interval differs')
  if (!same(summary.tokenOut.address, expected.tokenOut)) reject('tokenOut differs')
  if (!same(summary.tokenIn.address, orderTokenIn)) reject('tokenIn differs')
  if (summary.wrapsNative !== sellsNative) reject('wrap flag differs')
  if (BigInt(summary.executionFeeRaw) * 10000n > per * MAX_EXECUTION_FEE_BPS)
    reject('execution fee above the ceiling')

  // --- steps: known ids only, in the documented order, create-order last ---
  if (!steps.length) reject('empty plan')
  const ids = steps.map((s) => s.id)
  if (new Set(ids).size !== ids.length) reject('duplicate step')
  let lastRank = -1
  steps.forEach((step, i) => {
    const rank = STEP_ORDER.indexOf(step.id)
    if (rank === -1) reject(`unknown step ${step.id}`)
    if (rank <= lastRank) reject('steps out of order')
    if (step.index !== i) reject('step index mismatch')
    lastRank = rank
  })
  if (ids[ids.length - 1] !== 'create-order') reject('create-order is not the last step')
  if (sellsNative !== ids.includes('wrap-native')) reject('wrap step mismatch')

  const approves = steps.filter((s) => s.id.startsWith('approve'))

  steps.forEach((step) => {
    const { chainId, to, data, valueRaw } = step.payload
    if (step.type !== 'TRANSACTION') reject(`unexpected step type ${step.type}`)
    if (chainId !== DCA_CHAIN_ID) reject('wrong chain')
    const value = BigInt(valueRaw || '0')

    if (step.id === 'wrap-native') {
      if (!same(to, DCA_WRAPPED_NATIVE)) reject('bad wrap target')
      if (data.toLowerCase() !== WRAP_NATIVE_CALLDATA) reject('wrap is not deposit()')
      if (value !== total) reject('bad wrap amount')
      return
    }

    if (step.id.startsWith('approve')) {
      if (!same(to, orderTokenIn)) reject('bad approve token')
      if (value !== 0n) reject('approve sends value')
      let spender: string
      let amount: bigint
      try {
        const decoded = ERC20_APPROVE_INTERFACE.decodeFunctionData('approve', data)
        spender = decoded[0]
        amount = decoded[1]
      } catch {
        return reject('approve calldata does not decode')
      }
      if (!same(spender, executor)) reject('bad spender')
      const isLastApprove = step === approves[approves.length - 1]
      if (isLastApprove) {
        // The approval covers every active order on this token plus the new one.
        if (amount < total) reject('approval below the order budget')
        if (amount !== BigInt(summary.allowanceAfterRaw)) reject('approval differs from summary')
        if (amount > currentAllowanceRaw + total) reject('approval above current allowance + order')
      } else if (amount !== 0n) {
        // Only the USDT-style reset may precede the real approval.
        reject('unexpected intermediate approval')
      }
      const cap = step.assertions?.approval?.maxAmountRaw
      if (cap !== undefined && amount > BigInt(cap)) reject('approval above its own cap')
      return
    }

    // create-order
    if (!same(to, executor)) reject('create-order targets another contract')
    if (value !== 0n) reject('create-order sends value')
    let parsed: ReturnType<typeof DCA_EXECUTOR_INTERFACE.parseTransaction>
    try {
      parsed = DCA_EXECUTOR_INTERFACE.parseTransaction({ data })
    } catch {
      return reject('create-order calldata does not decode')
    }
    if (!parsed || parsed.name !== 'createOrder') reject('forbidden executor call')
    const p = parsed!.args[0]
    if (!same(p.tokenIn, orderTokenIn)) reject('order tokenIn differs')
    if (!same(p.tokenOut, expected.tokenOut)) reject('order tokenOut differs')
    if (BigInt(p.amountPerExecution) !== per) reject('order amount differs')
    if (Number(p.totalExecutions) !== expected.totalExecutions) reject('order count differs')
    if (Number(p.interval) !== expected.interval) reject('order interval differs')
    // `expected` is the number the form showed and sent, also when the backend
    // picked it: the order is never sent without an explicit slippage.
    if (Number(p.maxSlippageBps) !== expected.maxSlippageBps) reject('order slippage differs')
    if (Number(p.maxSlippageBps) > Math.min(config.maxSlippageBps, MAX_DCA_SLIPPAGE_BPS))
      reject('order slippage above the maximum')
    if (Number(p.maxFeeBps) !== config.feeBps) reject('order fee cap differs')
    if (BigInt(p.maxExecutionFee) !== BigInt(summary.executionFeeRaw))
      reject('order execution fee cap differs')
    if (Number(p.startTime) !== expected.startTime) reject('order start differs')
    const deadline = Number(p.deadline)
    if (deadline <= nowSeconds) reject('order deadline in the past')
    if (deadline > nowSeconds + config.limits!.maxDuration + DEADLINE_CLOCK_SLACK)
      reject('order deadline beyond max duration')
  })
}

const decodeApprove = (data: string): { spender: string; amount: bigint } => {
  try {
    const decoded = ERC20_APPROVE_INTERFACE.decodeFunctionData('approve', data)
    return { spender: decoded[0], amount: decoded[1] }
  } catch {
    return reject('approve calldata does not decode')
  }
}

/**
 * Check a cancel plan (`cancel-order`, then approvals that lower the allowance)
 * against the order and the executor pinned in this build. The approval may
 * only LOWER the allowance: cancelling is never a reason to grant more.
 */
export function verifyDcaCancelPlan({
  steps,
  summary,
  orderId,
  orderTokenIn,
  pinnedExecutor,
  currentAllowanceRaw
}: {
  steps: DcaExecutionStep[]
  summary: DcaCancelSummary
  orderId: string
  /** The order's `tokenIn` as read on-chain (`getOrder`), not from the API. */
  orderTokenIn: string
  pinnedExecutor: string
  /** The owner's allowance to the executor on `orderTokenIn`, read on-chain. */
  currentAllowanceRaw: bigint
}): void {
  const executor = pinnedExecutor.toLowerCase()
  if (!steps.length) reject('empty plan')
  if (steps[0]!.id !== 'cancel-order') reject('cancel-order is not the first step')

  const ids = steps.map((s) => s.id)
  if (new Set(ids).size !== ids.length) reject('duplicate step')
  const approves = steps.slice(1)
  approves.forEach((step, i) => {
    if (step.id !== (i === 0 ? 'approve' : `approve-${i + 1}`)) reject(`unknown step ${step.id}`)
  })
  if (approves.length > 2) reject('too many approvals')

  steps.forEach((step, i) => {
    const { chainId, to, data, valueRaw } = step.payload
    if (step.type !== 'TRANSACTION') reject(`unexpected step type ${step.type}`)
    if (step.index !== i) reject('step index mismatch')
    if (chainId !== DCA_CHAIN_ID) reject('wrong chain')
    if (BigInt(valueRaw || '0') !== 0n) reject('step sends value')

    if (step.id === 'cancel-order') {
      if (!same(to, executor)) reject('cancel-order targets another contract')
      let parsed: ReturnType<typeof DCA_EXECUTOR_INTERFACE.parseTransaction>
      try {
        parsed = DCA_EXECUTOR_INTERFACE.parseTransaction({ data })
      } catch {
        return reject('cancel-order calldata does not decode')
      }
      if (!parsed || parsed.name !== 'cancelOrder') reject('forbidden executor call')
      if (BigInt(parsed!.args[0]) !== BigInt(orderId)) reject('wrong orderId')
      return
    }

    if (!same(to, orderTokenIn)) reject('bad approve token')
    const { spender, amount } = decodeApprove(data)
    if (!same(spender, executor)) reject('bad spender')
    if (step === approves[approves.length - 1]) {
      if (amount > currentAllowanceRaw) reject('cancel raises the allowance')
      if (amount !== BigInt(summary.allowanceAfterRaw)) reject('approval differs from summary')
    } else if (amount !== 0n) {
      reject('unexpected intermediate approval')
    }
    const cap = step.assertions?.approval?.maxAmountRaw
    if (cap !== undefined && amount > BigInt(cap)) reject('approval above its own cap')
  })
}
